import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiX,
  FiBookOpen,
  FiTarget,
  FiEye,
  FiSearch,
  FiFilter,
  FiCheckCircle,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const fiveSteps = [
  {
    id: 1,
    title: 'Preview the Questions',
    icon: <FiEye />,
    description:
      'Read the questions (not answer choices) before reading the passage to know what to look for.',
    detailExplanation:
      "This step helps you read with purpose. You'll know whether to focus on main ideas, specific details, or the author's tone.",
    example:
      'If you see "What is the main purpose of this passage?", you know to pay special attention to thesis statements and overall structure.',
    tips: [
      'Read question stems only, not answer choices',
      'Note what type of information each question asks for',
      "Don't spend more than 30 seconds on this step",
    ],
    commonMistakes: [
      'Reading answer choices (wastes time and can confuse you)',
      'Trying to memorize all questions',
      'Spending too much time previewing',
    ],
  },
  {
    id: 2,
    title: 'Read Actively for Structure',
    icon: <FiSearch />,
    description:
      'Read the passage while identifying its organization and main components.',
    detailExplanation:
      'Focus on how ideas connect rather than memorizing details. Look for transition words and the logical flow.',
    example:
      'Notice phrases like "However," "In contrast," "Furthermore" - these signal important structural relationships.',
    tips: [
      'Identify the main point in the first paragraph',
      'Note how each paragraph relates to the main point',
      'Pay attention to transition words and phrases',
      "Don't get bogged down in technical details",
    ],
    commonMistakes: [
      'Reading too slowly and getting lost in details',
      'Ignoring paragraph structure',
      "Not noting the author's attitude or tone",
    ],
  },
  {
    id: 3,
    title: 'Identify the Main Point',
    icon: <FiTarget />,
    description:
      'Determine what the author is trying to convince you of or explain.',
    detailExplanation:
      'The main point is usually the thesis or central argument. It may be stated explicitly or need to be inferred.',
    example:
      'In a passage about climate change, the main point might be "Current climate models underestimate the rate of change."',
    tips: [
      'Look for thesis statements in introduction or conclusion',
      'Ask "What is the author\'s main message?"',
      'Distinguish between main point and supporting details',
      "Consider the author's overall attitude",
    ],
    commonMistakes: [
      'Confusing a detail with the main point',
      'Choosing something too broad or too narrow',
      'Missing implied main points',
    ],
  },
  {
    id: 4,
    title: 'Eliminate Wrong Answers',
    icon: <FiFilter />,
    description:
      'Use process of elimination to remove obviously incorrect choices.',
    detailExplanation:
      "Often it's easier to identify wrong answers than the right one. Look for common trap patterns.",
    example:
      'If the passage is neutral, eliminate answers that suggest strong positive or negative emotions.',
    tips: [
      'Cross out clearly wrong answers first',
      'Look for answers that are too extreme',
      'Eliminate answers not supported by the passage',
      'Watch for answers that distort information',
    ],
    commonMistakes: [
      'Not eliminating obviously wrong answers',
      'Falling for attractive but incorrect choices',
      'Not being systematic in elimination',
    ],
  },
  {
    id: 5,
    title: 'Select the Best Choice',
    icon: <FiCheckCircle />,
    description:
      'Choose the answer that best fits the question and is fully supported by the passage.',
    detailExplanation:
      'Compare remaining choices carefully. The correct answer should be 100% supported by the passage.',
    example:
      'Between two similar answers, choose the one that matches the scope and tone of the passage.',
    tips: [
      'Compare remaining answers carefully',
      'Ensure your choice is fully supported',
      'Match the scope of the question',
      'Trust your systematic process',
    ],
    commonMistakes: [
      'Second-guessing a systematic approach',
      'Choosing answers that are only partially correct',
      'Not verifying the answer against the passage',
    ],
  },
];

const practicePassage = {
  title: 'Practice Passage: The 5-Step Process in Action',
  text: `The widespread adoption of renewable energy technologies has been hailed as a crucial step toward environmental sustainability. Solar and wind power installations have increased dramatically over the past decade, driven by declining costs and supportive government policies. However, this transition is not without challenges.

One significant obstacle is the intermittent nature of renewable energy sources. Unlike traditional power plants that can generate electricity consistently, solar and wind installations depend on weather conditions. This variability creates complications for power grid management and requires sophisticated energy storage solutions.

Despite these challenges, many energy experts remain optimistic about the future of renewables. They argue that technological advances in battery storage and smart grid systems will eventually overcome current limitations. Furthermore, the environmental benefits of reducing fossil fuel dependence far outweigh the temporary inconveniences of the transition period.`,
  questions: [
    {
      id: 1,
      question: 'What is the primary purpose of this passage?',
      type: 'Main Idea',
      options: [
        'To advocate for immediate adoption of renewable energy',
        'To explain why renewable energy will never replace fossil fuels',
        'To present both benefits and challenges of renewable energy adoption',
        'To criticize government policies supporting renewable energy',
      ],
      correct: 2,
      stepByStepSolution: {
        step1:
          'This is a main purpose question - we need to identify the overall message.',
        step2:
          'The passage structure: intro (benefits), paragraph 2 (challenges), paragraph 3 (optimistic outlook despite challenges).',
        step3:
          'Main point: Renewable energy adoption has benefits but also faces challenges that experts believe can be overcome.',
        step4:
          "Eliminate: A (too strong advocacy), B (passage is optimistic), D (doesn't match content).",
        step5:
          'C best captures the balanced presentation of both benefits and challenges.',
      },
    },
    {
      id: 2,
      question:
        'According to the passage, what makes renewable energy challenging for power grid management?',
      type: 'Detail',
      options: [
        'High installation costs',
        'Government policy requirements',
        'Weather-dependent variability',
        'Lack of technological expertise',
      ],
      correct: 2,
      stepByStepSolution: {
        step1: 'This asks for a specific detail mentioned in the passage.',
        step2: 'Looking at paragraph 2, which discusses challenges.',
        step3:
          'The passage states intermittent nature depends on weather conditions.',
        step4:
          'Eliminate: A (costs mentioned as declining), B (policies are supportive), D (not mentioned).',
        step5:
          'C directly matches "depend on weather conditions" creating "complications for power grid management."',
      },
    },
  ],
};

export default function FiveStepProcessLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [showSolution, setShowSolution] = useState({});
  const [currentSolutionStep, setCurrentSolutionStep] = useState({});

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setPracticeAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const showStepBySolution = (questionId) => {
    setShowSolution((prev) => ({
      ...prev,
      [questionId]: true,
    }));
    setCurrentSolutionStep((prev) => ({
      ...prev,
      [questionId]: 1,
    }));
  };

  const nextSolutionStep = (questionId) => {
    setCurrentSolutionStep((prev) => ({
      ...prev,
      [questionId]: Math.min((prev[questionId] || 1) + 1, 5),
    }));
  };

  const toggleSolution = (questionId) => {
    setShowSolution((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">The Systematic 5-Step Approach</h2>

      <div className="lesson-intro">
        <p>
          This proven 5-step process will help you approach every Reading
          Comprehension question systematically and efficiently. Following these
          steps reduces errors and saves time by giving you a clear framework
          for analysis.
        </p>
      </div>

      <div className="question-types-grid">
        {fiveSteps.map((step) => (
          <div
            key={step.id}
            onClick={() => setSelectedStep(step)}
            className={`question-type-card ${selectedStep?.id === step.id ? 'selected' : ''}`}
          >
            <div className="type-header">
              <div className="type-number">{step.id}</div>
              <h3 className="type-title">{step.title}</h3>
            </div>
            <p className="type-description">{step.description}</p>
          </div>
        ))}
      </div>

      {selectedStep && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            <FiTarget />
            Step {selectedStep.id}: {selectedStep.title}
          </h3>

          <div className="analysis-section">
            <h4>Why This Step Matters:</h4>
            <div className="strategy-box">{selectedStep.detailExplanation}</div>
          </div>

          <div className="analysis-section">
            <h4>How to Execute:</h4>
            <ul className="tips-list">
              {selectedStep.tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>

          <div className="analysis-section">
            <h4>Example in Action:</h4>
            <div
              style={{
                background: 'var(--liwaza-surface-container-highest)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
              }}
            >
              <p
                style={{
                  fontStyle: 'italic',
                  color: 'var(--liwaza-on-surface-variant)',
                  marginBottom: '0.5rem',
                }}
              >
                "{selectedStep.example}"
              </p>
            </div>
          </div>

          <div className="analysis-section">
            <h4 className="tips-header">Success Tips:</h4>
            <ul className="tips-list">
              {selectedStep.tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Practice: Apply the 5-Step Process</h2>

      <div className="practice-passage">
        <h3 className="passage-title">Practice Passage</h3>

        <div className="passage-text">
          {practicePassage.text.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        {practicePassage.questions.map((question) => (
          <div key={question.id} style={{ marginBottom: '2rem' }}>
            <h4
              style={{
                color: 'var(--liwaza-on-surface)',
                marginBottom: '1rem',
                fontSize: '1.1rem',
              }}
            >
              Question {question.id}: {question.question}
            </h4>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(question.id, index)}
                  style={{
                    background:
                      practiceAnswers[question.id] === index
                        ? 'var(--liwaza-primary-container)'
                        : 'var(--liwaza-surface-container-highest)',
                    color:
                      practiceAnswers[question.id] === index
                        ? 'var(--liwaza-on-primary-container)'
                        : 'var(--liwaza-on-surface)',
                    border:
                      practiceAnswers[question.id] === index
                        ? '2px solid var(--liwaza-primary)'
                        : '1px solid var(--liwaza-outline-variant)',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {String.fromCharCode(65 + index)}. {option}
                </button>
              ))}
            </div>

            {practiceAnswers[question.id] !== undefined && (
              <button
                onClick={() => toggleSolution(question.id)}
                className="show-explanation-btn"
              >
                {showSolution[question.id]
                  ? 'Hide Step-by-Step Solution'
                  : 'Show Step-by-Step Solution'}
              </button>
            )}

            {showSolution[question.id] && question.stepByStepSolution && (
              <div className="explanation-box">
                <h5
                  style={{
                    color: 'var(--liwaza-on-primary-container)',
                    marginBottom: '1rem',
                    fontSize: '1rem',
                  }}
                >
                  5-Step Solution Process:
                </h5>

                {Object.entries(question.stepByStepSolution).map(
                  ([stepKey, stepContent], stepIndex) => (
                    <div
                      key={stepIndex}
                      style={{
                        background: 'var(--liwaza-surface-container)',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid var(--liwaza-outline-variant)',
                      }}
                    >
                      <h6
                        style={{
                          color: 'var(--liwaza-primary)',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <span
                          style={{
                            background: 'var(--liwaza-primary)',
                            color: 'white',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                          }}
                        >
                          {stepIndex + 1}
                        </span>
                        Step {stepIndex + 1}: {stepKey.replace('step', 'Step ')}
                      </h6>
                      <p
                        style={{
                          color: 'var(--liwaza-on-surface)',
                          margin: 0,
                          fontSize: '0.9rem',
                        }}
                      >
                        {stepContent}
                      </p>
                    </div>
                  )
                )}

                <div
                  style={{
                    background:
                      practiceAnswers[question.id] === question.correct
                        ? 'var(--liwaza-success-container)'
                        : 'var(--liwaza-error-container)',
                    color:
                      practiceAnswers[question.id] === question.correct
                        ? 'var(--liwaza-on-success-container)'
                        : 'var(--liwaza-on-error-container)',
                    padding: '1rem',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {practiceAnswers[question.id] === question.correct ? (
                    <>
                      <FiCheck />
                      <strong>Correct!</strong> You selected:{' '}
                      {String.fromCharCode(65 + question.correct)}.{' '}
                      {question.options[question.correct]}
                    </>
                  ) : (
                    <>
                      <FiX />
                      <strong>Incorrect.</strong> The correct answer is:{' '}
                      {String.fromCharCode(65 + question.correct)}.{' '}
                      {question.options[question.correct]}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Process Summary */}
      <div className="lesson-intro">
        <h4
          style={{
            color: 'var(--liwaza-on-primary-container)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiCheckCircle />
          Master the Process
        </h4>
        <ul
          style={{
            color: 'var(--liwaza-on-primary-container)',
            paddingLeft: '1rem',
            lineHeight: 1.6,
          }}
        >
          <li>Practice this process until it becomes automatic</li>
          <li>Time yourself - aim to complete each step efficiently</li>
          <li>Don't skip steps, even when you think you know the answer</li>
          <li>Use this framework for ALL Reading Comprehension questions</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiCheckCircle />
          Lesson 2: The 5-Step Process
        </h1>
        <p className="lesson-subtitle">
          Master the systematic approach that will guide you through every
          Reading Comprehension question with confidence and efficiency. This
          proven method reduces errors and saves valuable time.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help with the 5-Step Process?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding the 5-Step Process for GRE Reading Comprehension"
          aicontext="This proven 5-step process will help you approach every Reading Comprehension question systematically and efficiently. Following these steps reduces errors and saves time by giving you a clear framework for analysis."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/question-types')
          }
          className="prev-lesson-button"
        >
          <FiArrowLeft /> Previous: Question Types
        </button>

        <button
          onClick={() =>
            navigate(
              '/exams/gre/reading-comprehension/lesson/passage-components'
            )
          }
          className="next-lesson-button"
        >
          Next: Passage Components <FiArrowRight />
        </button>
      </div>
    </div>
  );
}
